import { z } from "zod";
// Impostazioni → «Ingressi dei Lead» (piano dell'ingresso del 10 ottobre 2026, I2, Task 4; piano M6, Task 17; mock-up
// `regole.html?sezione=ingressi`): le porte da cui i Lead entrano da soli. Il sito e le landing (`/api/intake/web/<chiave>`) e
// GoHighLevel (`/api/intake/ghl/<chiave>`) hanno un indirizzo con una chiave segreta; i moduli di Meta (piano del 12 ottobre 2026,
// I3, D1) li legge Gioia365 ogni minuto da una pagina Facebook collegata con «Collega la pagina Facebook».
// Ogni persona attiva vede la sezione (in sola lettura senza `settings.lead_intake`); la cambia, copia e cambia gli indirizzi, fa
// entrare un Lead di prova e vede «Gli ultimi Lead arrivati» soltanto chi ha `settings.lead_intake` (di partenza il Super admin
// dell'azienda) e il contesto di piattaforma. L'indirizzo intero non è mai in queste risposte: soltanto in quella di «Copia
// l'indirizzo».
//
// Gli elenchi ripetono quelli del database (`intake_connections`, `intake_inbox`) e del dominio, che i contratti non importano.
/** Le porte con un indirizzo. */
export const LEAD_INTAKE_DOORS = ["web", "ghl"];
/** Gli ingressi di «Gli ultimi Lead arrivati», compresi i moduli di Meta. */
export const LEAD_INTAKE_PROVIDERS = ["meta", "web", "ghl"];
/** «Chi diventa owner» di un Lead nuovo da GoHighLevel: il tag «Owner: Nome Cognome» e poi la regola, oppure sempre la regola. */
export const LEAD_INTAKE_OWNER_FROM = ["tag", "rule"];
export const LEAD_INTAKE_STATES = ["pending", "done", "failed"];
export const LEAD_INTAKE_RESULTS = ["created", "updated", "discarded", "duplicate"];
/** Da dove viene l'owner di un arrivo: scelto, dal tag, dalla regola, quello di prima che resta, nessuno. */
export const LEAD_INTAKE_OWNER_VIAS = ["chosen", "tag", "rule", "kept", "none"];
/** Quanti siti al più possono mandare i moduli. */
export const MAX_LEAD_INTAKE_ORIGINS = 20;
/** Quante righe ha «Gli ultimi Lead arrivati». */
export const LEAD_INTAKE_RECENT_LIMIT = 50;
/**
 * L'origine di un sito che manda i moduli, scritta esattamente come la manda il browser: `https://www.example.it` (oppure `http://…`
 * per una prova sul proprio computer), senza percorso, senza `/` in fondo, in minuscolo.
 */
export function isLeadIntakeOrigin(value) {
    try {
        const url = new URL(value);
        return (url.protocol === "https:" || url.protocol === "http:") && url.origin === value;
    }
    catch {
        return false;
    }
}
export const leadIntakeOriginSchema = z.string().max(200).refine(isLeadIntakeOrigin);
/** Una porta: se c'è, se è accesa, l'indirizzo con la chiave nascosta («…/k7Q2…mX9a») e la versione del collegamento. */
const doorSchema = z.object({
    exists: z.boolean(),
    active: z.boolean(),
    address: z.string().nullable(),
    version: z.number().int().nullable(),
});
/**
 * `GET /api/settings/lead-intake` e la risposta di `PUT` e di «Cambia l'indirizzo»: le due porte, se chi chiede le può cambiare
 * (`canEdit`), se può far entrare un Lead di prova (`testEntry`: con `canEdit`, soltanto in un'azienda di prova, slug `prova-`).
 */
/** Un identificativo di Meta (una pagina): cifre. */
export const metaObjectIdSchema = z.string().regex(/^[0-9]{1,32}$/);
/**
 * Il riquadro dei moduli di Meta: se il server ha Meta (le variabili `META_*`), i valori pubblici per l'SDK di Facebook (l'app e la
 * versione della Graph API), se una pagina è collegata, quale e da quando, la versione del collegamento (`null` se non c'è mai stato)
 * e l'ultimo giro di lettura con il codice dell'errore, se non è riuscito (`190`: Meta non accetta più il collegamento). Mai il
 * gettone della pagina.
 */
export const leadIntakeMetaSchema = z.object({
    available: z.boolean(),
    sdk: z.object({ appId: z.string(), graphVersion: z.string() }).nullable(),
    connected: z.boolean(),
    page: z.object({ id: z.string(), name: z.string() }).nullable(),
    connectedAt: z.string().nullable(),
    version: z.number().int().nullable(),
    lastRound: z.object({ at: z.string(), error: z.string().nullable() }).nullable(),
});
export const leadIntakeSettingsResponseSchema = z.object({
    canEdit: z.boolean(),
    testEntry: z.boolean(),
    web: doorSchema.extend({ allowedOrigins: z.array(z.string()) }),
    ghl: doorSchema.extend({ ownerFrom: z.enum(LEAD_INTAKE_OWNER_FROM) }),
    meta: leadIntakeMetaSchema,
});
/**
 * `PUT /api/settings/lead-intake`: le due porte intere, ognuna con la versione letta (`null` per una porta che non c'è ancora: nasce,
 * con il suo indirizzo, al primo salvataggio che la cambia). Una versione diversa è `409 version_conflict`.
 */
export const saveLeadIntakeSettingsBodySchema = z.strictObject({
    web: z.strictObject({
        active: z.boolean(),
        allowedOrigins: z.array(leadIntakeOriginSchema).max(MAX_LEAD_INTAKE_ORIGINS),
        expectedVersion: z.number().int().min(1).nullable(),
    }),
    ghl: z.strictObject({
        active: z.boolean(),
        ownerFrom: z.enum(LEAD_INTAKE_OWNER_FROM),
        expectedVersion: z.number().int().min(1).nullable(),
    }),
});
/** `POST /api/settings/lead-intake/copy-address` e `POST …/rotate`: la porta. */
export const leadIntakeDoorBodySchema = z.strictObject({ door: z.enum(LEAD_INTAKE_DOORS) });
/** La risposta di «Copia l'indirizzo»: l'indirizzo intero, con la chiave. */
export const leadIntakeAddressResponseSchema = z.object({ door: z.enum(LEAD_INTAKE_DOORS), address: z.string() });
/**
 * Un arrivo di «Gli ultimi Lead arrivati»: da dove, quando è arrivato, lo stato (da lavorare, lavorato, non riuscito dopo i
 * tentativi), l'esito con il motivo (di uno scarto; perché l'owner non è venuto dal tag; un modulo senza il tempo di compilazione), il
 * Lead (anche quello della prima volta di un doppio invio), l'owner e da dove viene, e i tentativi fatti.
 */
export const leadIntakeArrivalSchema = z.object({
    id: z.string(),
    provider: z.enum(LEAD_INTAKE_PROVIDERS),
    receivedAt: z.string(),
    state: z.enum(LEAD_INTAKE_STATES),
    result: z.enum(LEAD_INTAKE_RESULTS).nullable(),
    reason: z.string().nullable(),
    lead: z.object({ id: z.string(), displayName: z.string().nullable() }).nullable(),
    owner: z.object({ membershipId: z.string(), displayName: z.string() }).nullable(),
    ownerVia: z.enum(LEAD_INTAKE_OWNER_VIAS).nullable(),
    attempts: z.number().int(),
});
/** `GET /api/settings/lead-intake/recent`: gli ultimi 50 arrivi dell'azienda, dal più recente. */
export const leadIntakeRecentResponseSchema = z.object({ arrivals: z.array(leadIntakeArrivalSchema) });
/** `POST /api/settings/lead-intake/test-entry` (senza corpo, o `{}`): l'arrivo del Lead di prova, già lavorato. */
export const leadIntakeTestEntryBodySchema = z.strictObject({});
export const leadIntakeTestEntryResponseSchema = z.object({ arrival: leadIntakeArrivalSchema });
/** Il gettone utente di breve durata dell'accesso di Facebook nel browser: va al server e non resta nel browser. */
const facebookAccessTokenSchema = z.string().min(1).max(4096);
/** `POST /api/settings/lead-intake/meta/pages`: il gettone; la risposta sono le pagine che la persona amministra, mai un gettone. */
export const leadIntakeMetaPagesBodySchema = z.strictObject({ accessToken: facebookAccessTokenSchema });
export const leadIntakeMetaPagesResponseSchema = z.object({ pages: z.array(z.object({ id: z.string(), name: z.string() })) });
/**
 * `POST /api/settings/lead-intake/meta/connect`: il gettone, la pagina scelta e la versione letta del collegamento (`null` se non c'è
 * ancora). La risposta è quella di `GET /api/settings/lead-intake`.
 */
export const leadIntakeMetaConnectBodySchema = z.strictObject({
    accessToken: facebookAccessTokenSchema,
    pageId: metaObjectIdSchema,
    expectedVersion: z.number().int().min(1).nullable(),
});
/** `POST /api/settings/lead-intake/meta/disconnect` («Scollega la pagina»): la versione letta. */
export const leadIntakeMetaDisconnectBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1) });
//# sourceMappingURL=lead-intake.js.map